import { z } from 'zod';
import type { HttpService } from '@web/data/services';

/**
 * Shapes mirror `MemberDetailRecordSchema` / `OrganizationRecordSchema` in
 * `apps/services/accounts/src/domain/entities`. They are intentionally redefined
 * locally — the accounts-api domain package is not shared (the web bundle
 * cannot depend on a Mongo-coupled server package), and `@voll-intelligence/
 * domain` is for cross-app shared schemas only.
 *
 * Keep these in sync with the server side; the response parsing will fail
 * loudly with a Zod ValidationError if the contract drifts.
 */
const MemberRoleSchema = z.enum(['owner', 'admin', 'member', 'guest']);
export type MemberRole = z.infer<typeof MemberRoleSchema>;

const MemberStatusSchema = z.enum(['active', 'inactive', 'suspended']);
export type MemberStatus = z.infer<typeof MemberStatusSchema>;

const MemberSourceSchema = z.enum(['jit', 'manual']);

/**
 * accounts-api may omit `features` (older release) or emit one this bundle does
 * not know yet. Values are carried as raw strings — never filtered — because
 * `setFeatures` replaces the whole set on the server: dropping an unknown one
 * here would revoke a grant a newer accounts made. Only the literal 'mcp' is
 * ever interpreted (`mcpAccessFor`).
 */
const MemberFeaturesSchema = z.array(z.string()).optional().default([]);

const MemberDetailSchema = z.object({
  id: z.string(),
  userId: z.string(),
  orgId: z.string(),
  role: MemberRoleSchema,
  status: MemberStatusSchema,
  source: MemberSourceSchema,
  name: z.string(),
  email: z.string(),
  domain: z.string(),
  jumpcloudId: z.string(),
  joinedAt: z.string(),
  lastSeen: z.string().nullable(),
  promotedBy: z.string().nullable(),
  promotedAt: z.string().nullable(),
  suspendedBy: z.string().nullable(),
  suspendedAt: z.string().nullable(),
  suspendedReason: z.string().nullable(),
  features: MemberFeaturesSchema,
});
export type MemberDetail = z.infer<typeof MemberDetailSchema>;

const MemberRecordSchema = z.object({
  id: z.string(),
  userId: z.string(),
  orgId: z.string(),
  role: MemberRoleSchema,
  features: MemberFeaturesSchema,
});

const MembersListResponseSchema = z.array(MemberDetailSchema);

const OrganizationRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  features: z.array(z.string()),
  allowedDomains: z.array(z.string()),
  jitProvisioning: z.object({ enabled: z.boolean() }),
});
export type OrganizationRecord = z.infer<typeof OrganizationRecordSchema>;

interface MembersRepository {
  /** Org metadata for the JIT banner (domains + enabled flag). */
  getOrg: (orgId: string) => Promise<OrganizationRecord>;
  /** Enriched list — drives the table, stats, and drawer. */
  listMembers: (orgId: string) => Promise<MemberDetail[]>;
  setRole: (orgId: string, userId: string, role: MemberRole) => Promise<void>;
  setStatus: (
    orgId: string,
    userId: string,
    input: { status: 'active' | 'suspended'; reason?: string },
  ) => Promise<void>;
  remove: (orgId: string, userId: string) => Promise<void>;
  setJitEnabled: (orgId: string, enabled: boolean) => Promise<OrganizationRecord>;
  setFeatures: (orgId: string, userId: string, features: readonly string[]) => Promise<void>;
}

export function createMembersRepository(accountsHttp: HttpService): MembersRepository {
  async function getOrg(orgId: string): Promise<OrganizationRecord> {
    return accountsHttp.get(`/orgs/${orgId}`, { schema: OrganizationRecordSchema });
  }

  async function listMembers(orgId: string): Promise<MemberDetail[]> {
    return accountsHttp.get(`/orgs/${orgId}/members`, { schema: MembersListResponseSchema });
  }

  async function setRole(orgId: string, userId: string, role: MemberRole): Promise<void> {
    await accountsHttp.patch(
      `/orgs/${orgId}/members/${userId}`,
      { role },
      { schema: MemberRecordSchema },
    );
  }

  async function setStatus(
    orgId: string,
    userId: string,
    input: { status: 'active' | 'suspended'; reason?: string },
  ): Promise<void> {
    await accountsHttp.patch(`/orgs/${orgId}/members/${userId}/status`, input, {
      schema: MemberRecordSchema,
    });
  }

  async function remove(orgId: string, userId: string): Promise<void> {
    await accountsHttp.delete(`/orgs/${orgId}/members/${userId}`);
  }

  async function setJitEnabled(orgId: string, enabled: boolean): Promise<OrganizationRecord> {
    return accountsHttp.patch(
      `/orgs/${orgId}`,
      { jitProvisioning: { enabled } },
      { schema: OrganizationRecordSchema },
    );
  }

  async function setFeatures(
    orgId: string,
    userId: string,
    features: readonly string[],
  ): Promise<void> {
    await accountsHttp.patch(
      `/orgs/${orgId}/members/${userId}/features`,
      { features },
      { schema: MemberRecordSchema },
    );
  }

  return { getOrg, listMembers, setRole, setStatus, remove, setJitEnabled, setFeatures };
}
